/**
 * StrategyImportService
 *
 * Imports AI generation output (AiGenerationOutput JSON) into Firestore
 * as strategy blocks and screens. Performs template merge with strategy
 * theme to produce full StepNodes from simplified AI content.
 *
 * Import types:
 * - Strategy: Multiple blocks with screens from AiGenerationOutput
 * - Block: Single block's screens from AiGenerationOutput
 *
 * @module strategy-blocks/services
 */

import { StrategyBlockService } from '@modules/firebase-core/services/StrategyBlockService';
import { StrategyStepNodeService } from '@modules/firebase-core/services/StrategyStepNodeService';
import { V2TemplateStructMerger } from '@modules/screen-utils/services/V2TemplateStructMerger';
import { createDefaultStepNode } from '@modules/themes/factories/defaultStepNodeFactory';
import type { AiGenerationOutput, SimplifiedV2Block, SimplifiedV2Content } from '@segmently/schema-models/simplified';
import type { StepNode, FlowDocument } from '@segmently/schema-models/v2';
import { doc, updateDoc } from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import { normalizeBlockActionToStrategySchema } from '@modules/strategy-blocks/utils/blockNavigationEdges';
import { buildScreenAiAssetSlots } from '@modules/strategy-blocks/utils/onboardingAssetDesign';
import { normalizeStrategyBlocksForFlowTransform } from '@modules/strategy-blocks/utils/strategyFlowDocumentTransformer';
import { sanitizeStrategyScreenForRegenerationInput } from '@modules/strategy-blocks/utils/assetPlanWorkspaceSanitizer';
import type { StrategyAssetWorkspaceMode } from './StrategyExportService';

// ============================================================================
// Types
// ============================================================================

export interface ImportResult {
  blocksCreated: number;
  screensCreated: number;
  errors: string[];
}

export interface StrategyImportOptions {
  assetWorkspace?: StrategyAssetWorkspaceMode;
}

export interface ValidatedImport {
  valid: boolean;
  type: 'strategy' | 'block' | 'raw';
  aiOutput: AiGenerationOutput;
  blockCount: number;
  screenCount: number;
  error?: string;
  assetWorkspace?: StrategyAssetWorkspaceMode;
}

// ============================================================================
// Service
// ============================================================================

export class StrategyImportService {
  private readonly merger = new V2TemplateStructMerger();

  /**
   * Validate and parse an import JSON file.
   * Accepts: raw AiGenerationOutput, strategy export envelope, or block export envelope.
   */
  static validate(data: unknown): ValidatedImport {
    if (!data || typeof data !== 'object') {
      return { valid: false, type: 'raw', aiOutput: {} as any, blockCount: 0, screenCount: 0, error: 'Invalid JSON' };
    }

    const obj = data as Record<string, unknown>;
    let aiOutput: AiGenerationOutput;

    // Check for envelope formats
    if (obj.__segmently_strategy_export__) {
      // Strategy export envelope → extract sourceData but we need AI output to import
      return { valid: false, type: 'strategy', aiOutput: {} as any, blockCount: 0, screenCount: 0,
        error: 'Strategy export contains request payload, not AI output. Export AI response separately or paste raw AiGenerationOutput.' };
    }

    if (obj.__segmently_block_export__) {
      const metadata = isRecord((obj as any).metadata) ? (obj as any).metadata : {};
      const payload = isRecord((obj as any).payload) ? (obj as any).payload : {};
      const currentBlock = isRecord((payload as any).currentBlock) ? (payload as any).currentBlock : null;
      const screens = Array.isArray((currentBlock as any)?.screens) ? (currentBlock as any).screens : [];
      if (metadata.assetWorkspace === 'portable' && currentBlock && screens.length > 0) {
        const aiOutput = {
          onboardingStrategySequence: [
            {
              ...currentBlock,
              name: (currentBlock as any).name || metadata.blockName || 'Portable block import',
              id: (currentBlock as any).id || metadata.blockId,
              blockType: (currentBlock as any).blockType || (payload as any).blockType || 'custom',
              screens,
            },
          ],
        } as unknown as AiGenerationOutput;
        return {
          valid: true,
          type: 'block',
          aiOutput,
          blockCount: 1,
          screenCount: screens.length,
          assetWorkspace: 'portable',
        };
      }
      return { valid: false, type: 'block', aiOutput: {} as any, blockCount: 0, screenCount: 0,
        error: 'Block export contains request payload, not AI output. Use raw AiGenerationOutput for import.' };
    }

    // Try raw AiGenerationOutput
    if (Array.isArray(obj.onboardingStrategySequence)) {
      aiOutput = obj as unknown as AiGenerationOutput;
    } else if (obj.result && typeof obj.result === 'object' && Array.isArray((obj.result as any).onboardingStrategySequence)) {
      // Wrapped in task result
      aiOutput = obj.result as AiGenerationOutput;
    } else {
      return { valid: false, type: 'raw', aiOutput: {} as any, blockCount: 0, screenCount: 0,
        error: 'JSON must contain onboardingStrategySequence array (AiGenerationOutput format)' };
    }

    const blocks = (aiOutput.onboardingStrategySequence ?? []) as SimplifiedV2Block[];
    const screenCount = blocks.reduce((sum, b) => sum + (b.screens?.length ?? 0), 0);

    return {
      valid: true,
      type: blocks.length > 1 ? 'strategy' : 'block',
      aiOutput,
      blockCount: blocks.length,
      screenCount,
    };
  }

  /**
   * Import AI output as multiple blocks in a strategy.
   * Creates block documents and screens with template merge.
   */
  async importStrategy(
    projectId: string,
    strategyId: string,
    aiOutput: AiGenerationOutput,
    themeFlowDocument?: FlowDocument | null,
    options: StrategyImportOptions = {},
  ): Promise<ImportResult> {
    const blocks = normalizeStrategyBlocksForFlowTransform(
      [...(aiOutput.onboardingStrategySequence ?? [])] as any,
      strategyId,
    ) as unknown as SimplifiedV2Block[];
    const errors: string[] = [];
    let blocksCreated = 0;
    let screensCreated = 0;

    const blockService = new StrategyBlockService(projectId, strategyId);
    const templateMap = this.buildTemplateMap(themeFlowDocument);
    const defaultLang = themeFlowDocument?.defaultLanguage || 'en-US';

    for (let i = 0; i < blocks.length; i++) {
      const blockData = blocks[i];
      try {
        // Create block document with ALL fields from SimplifiedV2Block
        const block = await blockService.create({
          name: blockData.name || `Block ${i + 1}`,
          entityId: blockData.entityId || blockData.id || crypto.randomUUID(),
          description: blockData.description || '',
          blockType: blockData.blockType || 'custom',
          customSubtype: blockData.customSubtype,
          instructions: blockData.instructions,
          firstScreenId: blockData.firstScreenId || '',
          functionMapping: blockData.functionMapping || [],
          order: blockData.order ?? i,
          position: (blockData as any).position || { x: i * 562, y: 0 },
          action: normalizeBlockActionToStrategySchema(blockData.action as any),
          isFirstBlock: blockData.isFirstBlock ?? (i === 0),
          isLaunchBlock: blockData.isFirstBlock ?? (i === 0),
          taskId: (blockData as any).taskId || null,
          blockContract: (blockData as any).blockContract || { outputAssignment: [] },
          dataUsage: (blockData as any).dataUsage || { dataMappingItems: [] },
          compileHints: (blockData as any).compileHints || {},
          additionalInfo: (blockData as any).additionalInfo || '',
          ...(options.assetWorkspace === 'portable' ? getPortableBlockAssetWorkspace(blockData as any) : {}),
        } as any);

        blocksCreated++;

        // Create AI-generated variables in strategy.variables
        await this.createAiGeneratedVariables(projectId, strategyId, blockData);

        // Import screens for this block
        const screenResult = await this.importBlockScreens(
          projectId,
          strategyId,
          block.id!,
          blockData,
          templateMap,
          defaultLang,
          options,
        );
        screensCreated += screenResult;
      } catch (err) {
        errors.push(`Block "${blockData.name}": ${(err as Error).message}`);
      }
    }

    return { blocksCreated, screensCreated, errors };
  }

  /**
   * Import AI output as screens for a single existing block.
   * Deletes existing screens, then creates new ones with template merge.
   */
  async importBlock(
    projectId: string,
    strategyId: string,
    blockId: string,
    aiOutput: AiGenerationOutput,
    themeFlowDocument?: FlowDocument | null,
    options: StrategyImportOptions = {},
  ): Promise<ImportResult> {
    const blocks = aiOutput.onboardingStrategySequence ?? [];
    if (blocks.length === 0) {
      return { blocksCreated: 0, screensCreated: 0, errors: ['No blocks in AI output'] };
    }

    const blockData = normalizeStrategyBlocksForFlowTransform([blocks[0] as any], strategyId)[0] as unknown as SimplifiedV2Block;
    const templateMap = this.buildTemplateMap(themeFlowDocument);
    const defaultLang = themeFlowDocument?.defaultLanguage || 'en-US';

    // Create AI-generated variables in strategy.variables
    await this.createAiGeneratedVariables(projectId, strategyId, blockData);

    // Delete existing screens
    const screenService = new StrategyStepNodeService(projectId, strategyId, blockId);
    const existing = await screenService.listScreens();
    for (const screen of existing) {
      await screenService.deleteScreen((screen as any).id);
    }

    // Import new screens
    const screensCreated = await this.importBlockScreens(
      projectId,
      strategyId,
      blockId,
      blockData,
      templateMap,
      defaultLang,
      options,
    );

    // Update block metadata
    const blockService = new StrategyBlockService(projectId, strategyId);
    await blockService.update(blockId, {
      name: blockData.name,
      description: blockData.description || '',
      blockType: blockData.blockType,
      customSubtype: blockData.customSubtype,
      instructions: blockData.instructions,
      firstScreenId: blockData.firstScreenId || '',
      functionMapping: blockData.functionMapping || [],
      compileHints: (blockData as any).compileHints || {},
      ...(options.assetWorkspace === 'portable' ? getPortableBlockAssetWorkspace(blockData as any) : {}),
    } as any);

    return { blocksCreated: 0, screensCreated, errors: [] };
  }

  // ============================================================================
  // Private helpers
  // ============================================================================

  /**
   * Create FunnelVariables in strategy.variables for AI-generated variables.
   * Scans blockContract.outputAssignment for variables with tags: ["ai-generated"]
   * and adds them to the strategy document using dot-notation update.
   */
  private async createAiGeneratedVariables(
    projectId: string,
    strategyId: string,
    blockData: SimplifiedV2Block,
  ): Promise<number> {
    const outputAssignment = (blockData as any).blockContract?.outputAssignment;
    if (!Array.isArray(outputAssignment) || outputAssignment.length === 0) return 0;

    // Filter to AI-generated variables (have tags including "ai-generated" OR have options defined)
    // Support both new format (id/name/type) and old format (variableId/variableName/variableType)
    const newVars = outputAssignment.filter((v: any) => {
      const varId = v.id || v.variableId;
      const varType = v.type || v.variableType;
      return v.tags?.includes('ai-generated') || (v.options?.length > 0 && varId && varType);
    });
    if (newVars.length === 0) return 0;

    const firestore = CoreFirebaseService.getInstance().firestore;
    const strategyRef = doc(firestore, `projects/${projectId}/strategies/${strategyId}`);

    // Build dot-notation updates for each variable (avoids merge:true stale key bug)
    // Support both new (id/name/type) and old (variableId/variableName/variableType) field names
    const updates: Record<string, unknown> = {};
    for (const varDef of newVars) {
      const varId = varDef.id || varDef.variableId;
      updates[`variables.${varId}`] = {
        id: varId,
        name: varDef.name || varDef.variableName || varId,
        type: varDef.type || varDef.variableType,
        required: varDef.required ?? false,
        description: varDef.description || `AI-generated for block "${blockData.name}"`,
        tags: varDef.tags || ['ai-generated'],
        taskIds: (blockData as any).taskId ? [(blockData as any).taskId] : [],
        options: (varDef.options || []).map((opt: any) => ({
          id: opt.id,
          label: opt.label,
        })),
        createdAt: new Date(),
      };
    }

    await updateDoc(strategyRef, updates);
    return newVars.length;
  }

  private buildTemplateMap(flowDocument?: FlowDocument | null): Map<string, StepNode> {
    const map = new Map<string, StepNode>();
    if (flowDocument?.screens) {
      for (const [, step] of Object.entries(flowDocument.screens)) {
        const node = step as StepNode;
        if (node.screenType) {
          map.set(node.screenType, node);
        }
      }
    }
    return map;
  }

  private async importBlockScreens(
    projectId: string,
    strategyId: string,
    blockId: string,
    blockData: SimplifiedV2Block,
    templateMap: Map<string, StepNode>,
    defaultLang: string,
    options: StrategyImportOptions = {},
  ): Promise<number> {
    const screenService = new StrategyStepNodeService(projectId, strategyId, blockId);
    const screens = blockData.screens ?? [];
    let count = 0;

    for (let i = 0; i < screens.length; i++) {
      const sourceScreen = screens[i] as any;
      const screen = options.assetWorkspace === 'portable'
        ? { ...sourceScreen }
        : sanitizeStrategyScreenForRegenerationInput(sourceScreen) as any;
      const screenType = screen.screenType || '';
      const simplified = screen.content as SimplifiedV2Content | undefined;

      let screenData: Record<string, unknown>;

      if (simplified && screenType) {
        // Merge with template
        const template = templateMap.get(screenType) || createDefaultStepNode(screenType);
        try {
          const fullNode = this.merger.merge(template, simplified, defaultLang);
          screenData = {
            ...(fullNode as unknown as Record<string, unknown>),
            id: screen.id,
            screenType,
            name: screen.name || screenType,
            aiContent: simplified,
          };
          const templateTags = Array.isArray((screenData as any).tags) ? (screenData as any).tags : [];
          const aiTags = Array.isArray((screen as any).tags) ? (screen as any).tags : [];
          if (templateTags.length > 0 || aiTags.length > 0) {
            screenData.tags = Array.from(new Set([...templateTags, ...aiTags].filter((tag) => typeof tag === 'string')));
          }
          // Preserve position from AI output (calculated by post-process script)
          if (screen.position) {
            screenData.position = screen.position;
          }
        } catch {
          // Merge failed — save raw (position preserved via spread)
          screenData = { ...screen } as Record<string, unknown>;
        }
      } else {
        // No simplified content or screenType — save raw (position preserved via spread)
        screenData = { ...screen } as Record<string, unknown>;
      }

      // Add metadata — preserve blockRole from AI output if present
      screenData.aiMeta = {
        ...(screen.aiMeta ?? {}),
        generationTimestamp: new Date().toISOString(),
        generationModel: 'imported',
        iterationCount: 1,
        blockRole: (screen.aiMeta as any)?.blockRole || screen.name || screenType,
      };
      screenData.order = i;

      const aiAssetSlots = options.assetWorkspace === 'portable' && isRecord(screen.aiAssets)
        ? screen.aiAssets as Record<string, unknown>
        : buildScreenAiAssetSlots(screen as any, simplified as any, {
            generationModel: 'imported',
          });
      if (Object.keys(aiAssetSlots).length > 0) {
        screenData.aiAssets = aiAssetSlots;
      }

      await screenService.addScreen(screenData as any);
      count++;
    }

    return count;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function getPortableBlockAssetWorkspace(block: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key of ['latestAssetPlanId', 'assetPlanVersion', 'assetPlanStatus', 'assetHealth']) {
    if (key in block) result[key] = block[key];
  }
  return result;
}
